import api from './index';
import { readFetchError } from '../utils/apiErrorUtils';
import {
  getAuthBearerHeaders,
  requireAuthToken,
} from '../utils/authUtils';

  const isTemporaryMessageId = (messageId) => {
    if (!messageId) return false;
    return String(messageId).trim().startsWith('msg-');
  };

const isFileLike = (value) =>
  typeof Blob !== 'undefined' && value != null && value instanceof Blob;

const STREAM_ATTACHMENT_ENDPOINT = '/messages/stream_attachment';

const appendFormValue = (formData, key, value) => {
  if (value === undefined) return;
  if (isFileLike(value)) {
    const name =
      typeof File !== 'undefined' && value instanceof File && value.name
        ? value.name
        : 'attachment';
    formData.append(key, value, name);
    return;
  }
  if (value === null) {
    formData.append(key, '');
    return;
  }
  if (typeof value === 'boolean') {
    formData.append(key, value ? 'true' : 'false');
    return;
  }
  formData.append(key, String(value));
};

const toStreamFormData = (payload = {}) => {
  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    return payload;
  }

  const formData = new FormData();
  const data =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload
      : {};

  appendFormValue(formData, 'conversationId', data.conversationId ?? '');
  appendFormValue(formData, 'modelId', data.modelId);
  appendFormValue(formData, 'contentText', data.contentText ?? '');
  appendFormValue(formData, 'silosId', data.silosId);
  appendFormValue(formData, 'use_web_search', data.use_web_search);
  appendFormValue(formData, 'clientId', data.clientId ?? '');
  appendFormValue(formData, 'projectId', data.projectId ?? '');
  appendFormValue(formData, 'globallyAvailable', data.globallyAvailable);
  if (data.attachment) {
    appendFormValue(formData, 'attachment', data.attachment);
  }

  return formData;
};

export const messageService = {
  // Shared stream implementation for all message stream endpoints.
  streamFromEndpoint: async (
    endpoint,
    payload,
    onChunk = null,
    onMetadata = null,
    onDelta = null,
    options = {},
  ) => {
    try {
      if (options?.requireAuth !== false) {
        requireAuthToken();
      }

      const isFormData =
        typeof FormData !== 'undefined' && payload instanceof FormData;
      const headers = getAuthBearerHeaders();
      if (isFormData) {
        delete headers['Content-Type'];
      }

      const response = await fetch(`${api.defaults.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: isFormData ? payload : JSON.stringify(payload),
        signal: options?.signal,
      });

        if (!response.ok) {
          const error = await readFetchError(
            response,
            response.status === 401
              ? "Authentication required. Please log in again."
              : undefined,
          );
          throw error;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let responseFormat = 'structured';

      let buffer = '';

      const processDataLine = (rawData) => {
        const data = rawData ?? '';
        const trimmed = data.trim();
        if (trimmed === '[DONE]') return;

        if (trimmed.startsWith('format:')) {
          if (onMetadata) {
            const formatMatch = trimmed.match(/format:\s*(\w+)/);
            if (formatMatch) {
              responseFormat = formatMatch[1];
              onMetadata({ format: responseFormat });
            }
          }
          return;
        }

        // Keep exact streamed payload as-is to preserve whitespace/newlines.
        const delta = data;
        fullResponse += delta;

        if (onDelta) {
          onDelta(delta);
        }

        if (onChunk) {
          onChunk(fullResponse);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line (\n\n). Each frame can contain multiple lines.
        while (true) {
          const frameEnd = buffer.indexOf('\n\n');
          if (frameEnd === -1) break;

          const frame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);

          const lines = frame.split('\n');
          const frameDataParts = [];
          for (const line of lines) {
            if (line.startsWith('data:')) {
              frameDataParts.push(line.slice(5));
              continue;
            }
            if (line.startsWith(':')) {
              continue;
            }
            // Tolerate non-standard SSE payloads that stream continuation lines
            // without repeating the `data:` prefix; preserve line breaks for markdown.
            if (frameDataParts.length > 0) {
              frameDataParts.push(line);
            }
          }
          if (frameDataParts.length > 0) {
            processDataLine(frameDataParts.join('\n'));
          }
        }
      }

      // Flush any remaining buffered data (in case server doesn't end with \n\n)
      if (buffer) {
        const lines = buffer.split('\n');
        const frameDataParts = [];
        for (const line of lines) {
          if (line.startsWith('data:')) {
            frameDataParts.push(line.slice(5));
            continue;
          }
          if (line.startsWith(':')) {
            continue;
          }
          if (frameDataParts.length > 0) {
            frameDataParts.push(line);
          }
        }
        if (frameDataParts.length > 0) {
          processDataLine(frameDataParts.join('\n'));
        }
      }

        return {
          contentText: fullResponse,
          response: fullResponse,
          format: responseFormat
        };
    } catch (error) {
      throw error;
    }
  },

  // Stream messages API with typing effect support (multipart / attachment).
  streamMessage: async (
    payload,
    onChunk = null,
    onMetadata = null,
    onDelta = null,
    options = {},
  ) => {
    return await messageService.streamFromEndpoint(
      STREAM_ATTACHMENT_ENDPOINT,
      toStreamFormData(payload),
      onChunk,
      onMetadata,
      onDelta,
      options,
    );
  },

  // Home previously used stream_home; both flows now use stream_attachment.
  streamHomeMessage: async (
    payload,
    onChunk = null,
    onMetadata = null,
    onDelta = null,
    options = {},
  ) => {
    return await messageService.streamMessage(
      payload,
      onChunk,
      onMetadata,
      onDelta,
      options,
    );
  },

    // Simple stream message without callbacks (for backward compatibility)
    streamMessageSimple: async (payload) => {
      return await messageService.streamMessage(payload);
    },
    
    // Get messages by conversation ID API
    getMessagesByConversationId: async (conversationId) => {
      const response = await api.get(`/messages/getByConversationId/${conversationId}`);
      return response.data;
    },

    viewAttachment: async (messageId, filename) => {
      requireAuthToken();
      const id = String(messageId ?? "").trim();
      const name = String(filename ?? "").trim();
      if (!id || isTemporaryMessageId(id) || !name) {
        throw new Error("messageId and filename are required");
      }

      const headers = getAuthBearerHeaders();
      delete headers["Content-Type"];

      const response = await fetch(
        `${api.defaults.baseURL}/messages/viewAttachment/${encodeURIComponent(id)}/${encodeURIComponent(name)}`,
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        throw await readFetchError(
          response,
          response.status === 401
            ? "Authentication required. Please log in again."
            : undefined,
        );
      }

      const buffer = await response.arrayBuffer();
      const contentType =
        response.headers.get("content-type")?.split(";")[0]?.trim() ||
        "application/octet-stream";
      const blob = new Blob([buffer], { type: contentType });
      const url = URL.createObjectURL(blob);

      return {
        blob,
        url,
        mimeType: contentType,
      };
    },

    feedbackMessage: async (data) => {
      const payload = { ...(data || {}) };

      if (isTemporaryMessageId(payload.messagesId)) {
        delete payload.messagesId;
      }

      if (!payload.messagesId && !payload.conversationId) {
        throw new Error('Feedback requires a persisted messagesId or conversationId');
      }

      const response = await api.put('/messages/feedback', payload);
      return response.data;
    },

    saveMessageNote: async (data) => {
      const payload = { ...(data || {}) };

      if (isTemporaryMessageId(payload.messagesId)) {
        delete payload.messagesId;
      }

      if (!payload.messagesId && !payload.conversationId) {
        throw new Error('Note requires a persisted messagesId or conversationId');
      }

      const response = await api.post('/messages/note', payload);
      return response.data;
    },

    isLockMessage: async (data) => {
      const payload = { ...(data || {}) };

      if (isTemporaryMessageId(payload.messagesId)) {
        delete payload.messagesId;
      }

      if (!payload.messagesId) {
        throw new Error('Lock requires a persisted messagesId');
      }

      if (payload.clientId == null) {
        throw new Error('Lock requires a clientId');
      }

      if (payload.projectId == null) {
        delete payload.projectId;
      }

      const response = await api.put('/messages/isLockMessage', payload);
      return response.data;
    },

    getLockedMessage: async (clientId, projectId) => {
      if (clientId == null || String(clientId).trim() === "") {
        throw new Error("clientId is required");
      }
      if (projectId == null || String(projectId).trim() === "") {
        throw new Error("projectId is required");
      }

      const response = await api.get(
        `/messages/getLockedMessage/${encodeURIComponent(clientId)}/${encodeURIComponent(projectId)}`,
      );
      return response.data;
    },
  };


